import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Snackbar, Alert, Button } from '@mui/material';
import { useVersionCheck } from 'src/hooks/useVersionCheck';

/**
 * Tiered "new version" UX:
 * - Non-breaking update -> dismissible info snackbar with a Refresh action.
 * - Breaking update     -> persistent warning; reload is deferred to the next
 *   navigation (so a mid-form user is never interrupted), plus a "Reload now" button.
 */
export default function VersionUpdateBanner() {
  const { updateAvailable, breaking } = useVersionCheck();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const breakingRef = useRef(false);

  useEffect(() => {
    breakingRef.current = breaking;
  }, [breaking]);

  // Breaking release: apply on the next navigation (deferred, non-disruptive).
  useEffect(() => {
    if (breakingRef.current) {
      window.location.reload();
    }
  }, [location.pathname]);

  if (!updateAvailable) return null;

  if (breaking) {
    return (
      <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload now
            </Button>
          }
        >
          A required update is available. It will apply automatically when you navigate.
        </Alert>
      </Snackbar>
    );
  }

  if (dismissed) return null;

  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setDismissed(true)}>
      <Alert
        severity="info"
        onClose={() => setDismissed(true)}
        action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        }
      >
        A new version is available.
      </Alert>
    </Snackbar>
  );
}
